import { FORM_ERROR } from 'final-form'
import { useRef } from 'react'
import { Form as RFFForm } from 'react-final-form'
import { getErrorLabel } from '@captaincause/lib/api/errors'
import { sleep } from '@captaincause/lib/sleep'
import { useOnMount } from '@captaincause/lib/useOnMount'
import { Toaster } from '../Toaster'
import type { ThrowableApiErrorData } from '@captaincause/lib/api/errors'
import type { Locales } from '@captaincause/types'
import type { FormApi } from 'final-form'
import type { CSSProperties, ReactNode } from 'react'
import type { FormRenderProps } from 'react-final-form'

export type RFFProps<V> = FormRenderProps<V>
export type FFFormApi<V> = FormApi<V, V>

export type FormSubmit<Values = never, Data = never> = (
  values: Values,
  form: FFFormApi<Values>,
) => Promise<Data | undefined>

export type FormSubmitSuccess<Values, Data> = (
  result: Data | undefined,
  form: FFFormApi<Values>,
  values: Values,
) => void | Promise<void>

export type FormSubmitFail<Values = never> = (
  error: ThrowableApiErrorData,
  values: Values,
  form: FFFormApi<Values>,
) => void | Promise<void>

export type FormChange<Values> = (
  values: Values,
  prevValues: Values | undefined,
  form: RFFProps<Values>,
) => void | Promise<void>

export type FormMount<Values> = (form: RFFProps<Values>) => void

type RenderProps<Values> =
  | ((
      form: RFFProps<Values> & { previousValues: Values | undefined },
    ) => ReactNode)
  | ReactNode

type FormProps<Values, Data> = {
  children: RenderProps<Values>
  className?: string
  style?: CSSProperties
  id?: string
  initialValues?: Values
  locale: Locales
  onChange?: FormChange<Values>
  onMount?: FormMount<Values>
  onSubmit: FormSubmit<Values, Data>
  onSubmitFail?: FormSubmitFail<Values>
  onSubmitSuccess?: FormSubmitSuccess<Values, Data>
  shouldPreventSubmitOnEnter?: boolean
  submitDelay?: number
  withToastOnFailure?: boolean
  fallbackErrorMessage?: string
}

type Object = { [key: string]: any }

function preventSubmitOnEnter(event: React.KeyboardEvent<HTMLFormElement>) {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement

  if (
    event.key === 'Enter' &&
    target.tagName !== 'TEXTAREA' &&
    target.contentEditable !== 'true'
  ) {
    event.preventDefault()
  }
}

const noop = () => {}
export function Form<Values, Data extends Object = never>({
  children,
  className = '',
  style = undefined,
  id = undefined,
  initialValues = undefined,
  locale,
  onChange = noop,
  onMount = noop,
  onSubmit,
  onSubmitFail = noop,
  onSubmitSuccess = noop,
  shouldPreventSubmitOnEnter = false,
  submitDelay = undefined,
  withToastOnFailure = false,
  fallbackErrorMessage = undefined,
}: FormProps<Values, Data>) {
  const formRef = useRef<RFFProps<Values> | null>(null)
  const previousValues = useRef<Values | null>(null)

  // TODO(TTH): This should have better typing, as we should ensure that
  // onSubmitSuccess takes a Data and not a Data | undefined
  const handleSubmit = async (values: Values, form: FFFormApi<Values>) => {
    const delay = submitDelay ?? 0

    try {
      const [result] = await Promise.all([onSubmit(values, form), sleep(delay)])

      await onSubmitSuccess(result, form, values)

      return undefined
    } catch (error: unknown) {
      await sleep(delay)

      const castedError = error as ThrowableApiErrorData

      if (withToastOnFailure) {
        Toaster({
          type: 'error',
          message: getErrorLabel(
            castedError?.data?.code ?? '400',
            locale,
            fallbackErrorMessage,
          ),
          id: castedError?.data?.code ?? '400',
        })
      }

      await onSubmitFail(castedError, values, form)

      return { [FORM_ERROR]: castedError.data?.error || castedError.message }
    }
  }

  useOnMount(() => {
    if (formRef.current) {
      onMount(formRef.current)
    }
  })

  return (
    <RFFForm id={id} onSubmit={handleSubmit} initialValues={initialValues}>
      {form => {
        previousValues.current = formRef.current?.values
          ? { ...formRef.current?.values }
          : null

        onChange(form.values, previousValues.current ?? undefined, form)

        formRef.current = form

        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <form
            id={id}
            className={className}
            style={style}
            onSubmit={form.handleSubmit}
            onKeyDown={
              shouldPreventSubmitOnEnter ? preventSubmitOnEnter : undefined
            }
            data-submitting={form.submitting}
            data-submit-delay={submitDelay}
            noValidate
          >
            {typeof children === 'function'
              ? children({
                  previousValues: previousValues.current ?? undefined,
                  ...form,
                })
              : children}
          </form>
        )
      }}
    </RFFForm>
  )
}
